import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { LoanResponse } from '@impact/data';
import { first, map, takeUntil } from 'rxjs/operators';

import { UsedCarFinancingTypes } from '../../../../../lib/used-car-details/used-car.models';
import { CheckoutFlowService } from '../../../../checkout/checkout-flow.service';
import { CheckoutService, UsedCarCheckoutFinancingTabsOrder } from '../../../../checkout/checkout.service';
import { Observable, Subject } from 'rxjs';
import { IRadioGroupItem } from '../../../../form-elements/radio-button/radio-button.component';
import { SettingsService, SiteIds } from '../../../../core/settings.service';

@Component({
    selector: 'app-checkout-step-payment',
    template: `
        <section class="flow-step">
            <ng-container *ngIf="exchangeCarExceedsCarValue">
                <section class="flow-step__header">
                    <h1 class="h4">
                        {{ 'checkout.payment_step____not_necessary____headline' | translate }}
                    </h1>
                    <p>{{ 'checkout.payment_step____not_necessary____description' | translate }}</p>
                </section>
                <section class="flow-step__content">
                    <app-button
                        (handleClick)="checkoutFlowService.nextStep()"
                        [loading]="(checkoutService.isCheckoutLoading$ | async) === true"
                        [title]="'checkout.payment_step____not_necessary____button' | translate"
                    ></app-button>
                </section>
            </ng-container>

            <ng-container *ngIf="!exchangeCarExceedsCarValue">
                <section class="flow-step__header">
                    <h1 class="h4">
                        {{ 'checkout.payment_step____headline' | translate }}
                    </h1>
                    <p>{{ 'checkout.payment_step____description' | translate }}</p>
                </section>

                <ng-container [ngSwitch]="site">
                    <ng-container *ngSwitchCase="siteIds.MOTORPOINT">
                        <app-tab-group *ngIf="checkoutService.getCheckoutData() | async as cd">
                            <app-tab [noContentWrapper]="true">
                                <app-checkout-step-payment-leasing
                                    [leasingCheckoutData]="leasingData"
                                    [allowLeasing]="overlayData?.allowLeasing"
                                    [leasingTypes]="leasingTypes$ | async"
                                    [leasingPurposes]="leasingPurposes$ | async">
                                </app-checkout-step-payment-leasing>
                            </app-tab>
                        </app-tab-group>
                    </ng-container>
                    <ng-container *ngSwitchDefault>
                        <app-tab-group *ngIf="checkoutService.getCheckoutData() | async as cd" [initialActiveIndex]="activeTabIndex">
                            <ng-container *ngFor="let financingType of usedCarFinancingTabsOrder">
                                <ng-container *ngIf="financingType === usedCarFinancingTypes.Cash">
                                    <app-tab [label]="'checkout.payment_step____cash_tab____title' | translate" [noContentWrapper]="true">
                                        <app-checkout-step-payment-cash></app-checkout-step-payment-cash>
                                    </app-tab>
                                </ng-container>
                                <ng-container *ngIf="financingType === usedCarFinancingTypes.Loan">
                                    <app-tab [label]="'checkout.payment_step____loan_tab____title' | translate" [noContentWrapper]="true">
                                        <app-checkout-step-payment-loan [loanData]="loanData"></app-checkout-step-payment-loan>
                                    </app-tab>
                                </ng-container>
                                <ng-container *ngIf="overlayData.allowLeasing && financingType === usedCarFinancingTypes.Leasing">
                                    <app-tab [label]="'checkout.payment_step____leasing_tab____title' | translate" [noContentWrapper]="true">
                                        <app-checkout-step-payment-leasing
                                            [leasingCheckoutData]="leasingData"
                                            [allowLeasing]="overlayData?.allowLeasing"
                                            [leasingTypes]="leasingTypes$ | async"
                                            [leasingPurposes]="leasingPurposes$ | async"
                                        ></app-checkout-step-payment-leasing>
                                    </app-tab>
                                </ng-container>
                            </ng-container>
                        </app-tab-group>
                    </ng-container>
                </ng-container>
            </ng-container>
        </section>
    `,
})
export class CheckoutStepPaymentComponent implements OnInit {
    @Input() loanData?: LoanResponse;
    @Input() leasingData?: any;
    @Input() overlayData: any;
    exchangeCarExceedsCarValue?: boolean;
    usedCarFinancingTabsOrder = UsedCarCheckoutFinancingTabsOrder;
    usedCarFinancingTypes = UsedCarFinancingTypes;
    activeTabIndex: number;

    site: string;
    siteIds = SiteIds;
    private readonly unsubscribe = new Subject<void>();

    leasingTypes$: Observable<IRadioGroupItem[]>;
    leasingPurposes$: Observable<IRadioGroupItem[]>;

    constructor(
        public checkoutService: CheckoutService,
        public checkoutFlowService: CheckoutFlowService,
        public settingsService: SettingsService,
        private cdr: ChangeDetectorRef
    ) {
        this.settingsService
        .get()
        .pipe(takeUntil(this.unsubscribe))
        .subscribe((settings) => {
            this.site = settings.siteNameId;
        });

        this.checkoutService
            .getCheckoutData()
            .pipe(first())
            .subscribe(({ product, exchangeCar }) => {
                if (product?.grossPrice && exchangeCar?.price) {
                    this.exchangeCarExceedsCarValue = exchangeCar?.price > product?.grossPrice;
                }
            });

        this.leasingTypes$ = settingsService.get().pipe(map((settings) => settings.leasingCalculator.leasingTypes.map((type: { label: string, key: string }) => ({
            label: type.label,
            value: type.key,
        }))));
        this.leasingPurposes$ = settingsService.get().pipe(map((settings) => settings.leasingCalculator.leasingPurposes.map((type: { label: string, key: string }) => ({
            label: type.label,
            value: type.key,
        }))));
    }

    ngOnInit() {
        this.activeTabIndex = this.usedCarFinancingTabsOrder.indexOf(this.overlayData.selectedCheckoutMethod);

        if (!this.overlayData.allowLeasing && this.activeTabIndex === 2) {
            this.activeTabIndex = 0;
        }

        this.cdr.detectChanges();
    }
}
